import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { captureException } from '../utils/sentry';

/**
 * Catches render errors so a single broken page doesn't white-screen the app.
 * Reports to Sentry when REACT_APP_SENTRY_DSN is configured.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Stale deploy: a lazily-loaded chunk no longer exists on the server
    // (hashes changed). A one-time reload fetches the fresh index.html and
    // matching chunks. Guarded so a genuinely broken build can't reload-loop.
    const msg = String(error?.message || '');
    const isChunkError = error?.name === 'ChunkLoadError'
      || /Loading (CSS )?chunk [^ ]* failed|Unexpected token '<'|Importing a module script failed|dynamically imported module/i.test(msg);
    if (isChunkError) {
      try {
        const KEY = 'el_chunk_reload_ts';
        const last = Number(sessionStorage.getItem(KEY) || 0);
        if (Date.now() - last > 60000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch { /* fall through to error UI */ }
    }
    captureException(error, {
      componentStack: info?.componentStack,
      boundary: this.props.name || 'root',
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === 'function') this.props.onReset();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Paper sx={{ p: 4, maxWidth: 440, textAlign: 'center' }} elevation={2}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              משהו השתבש
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              אירעה שגיאה בלתי צפויה. אפשר לנסות שוב או לרענן את הדף.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={this.handleReset}>נסה שוב</Button>
              <Button variant="outlined" onClick={() => window.location.assign('/')}>לדף הבית</Button>
            </Box>
          </Paper>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
